/**
 * A country (region) within a Shopify Market on a bridge `selected_markets` entry.
 * When `shopify_markets_multiple_countries` is enabled, email subscription groups are
 * configured per country via the fields below (not on the parent market).
 */
export interface ShopifyBridgeMarketCountry {
  code: string;
  name: string;
  /** Persisted Braze-created or marketer-selected default email group for this country */
  email_subscription_group_id?: string;
  /** PATCH input only: creates a new default email group when saved */
  email_subscription_group_name?: string;
  additional_email_subscription_group_ids?: string[];
}

export interface ShopifyBridgeMarketRootUrl {
  locale: string;
  url: string;
}

/**
 * Market details stored on a Shopify bridge `selected_markets` hash (GET and PATCH).
 * `catalog_id` and `catalog_status` may be absent on bridges persisted before those fields were added.
 * Email subscription groups are stored on `countries[]` when `shopify_markets_multiple_countries` is enabled.
 */
export interface ShopifyBridgeMarketDetails {
  name: string;
  handle: string;
  catalog_id?: string;
  /** Import status for this market's catalog (mirrors BridgeStatus values) */
  catalog_status?: (typeof BridgeStatus)[keyof typeof BridgeStatus] | null;
  countries: ShopifyBridgeMarketCountry[];
  default_locale?: string;
  /** All locales for this market, populated when shopify_markets_multiple_countries is on. First entry matches default_locale. */
  locales?: string[];
  /** ISO 4217 code from Shopify Market.currencySettings.baseCurrency (e.g. CAD); absent on older persisted bridges */
  currency_code?: string;
  /** From Shopify MarketWebPresence.rootUrls; absent on bridges persisted before localized URLs */
  root_urls?: ShopifyBridgeMarketRootUrl[];
  /**
   * Legacy market-level default email group. New bridges with `shopify_markets_multiple_countries`
   * use `countries[].email_subscription_group_*` instead; this field may still be present on older
   * persisted bridges until migrated.
   */
  email_subscription_group_id?: string;
  /** Legacy PATCH input for market-level default group creation */
  email_subscription_group_name?: string;
  /** Legacy market-level additional email groups */
  additional_email_subscription_group_ids?: string[];
  /** Marketer-selected SMS groups for this market, applied on top of the global SMS group */
  sms_subscription_group_ids?: string[];
}

/** Shopify Market GID => market details */
export type ShopifyBridgeSelectedMarkets = Record<string, ShopifyBridgeMarketDetails>;

/** Row in the bridge's `synced_segments`; name is null for segments synced before name persistence existed */
export interface ShopifyBridgeSyncedSegment {
  id: string;
  name: string | null;
  sync_status: string;
}

export type ShopifySegmentSyncResult = "initiated" | "syncing" | "queued" | "synced" | "invalid";

/** Per-segment outcome entry when the bridge PATCH response includes `segment_sync_results`. */
export interface ShopifySegmentSyncResultEntry {
  segment_id: string;
  /** Free-form so an unrecognized result round-trips instead of failing validation */
  result: string;
}

export interface AlloysPartnersShopifyAppGroupIdBridgesBridgeIdResult {
  bridges: BridgeGetObject[];
}

export interface BridgeGetObject {
  id: string;
  external_id: string;
  status: (typeof BridgeStatus)[keyof typeof BridgeStatus];
  catalog_status: (typeof BridgeStatus)[keyof typeof BridgeStatus] | null;
  catalog_error_type: string | null;
  shop_name: string;
  processing: boolean;
  connected_at: string | null;
  disconnected_at: string | null;
  enabled_user_events: boolean | null;
  external_id_type: string | null;
  custom_id_endpoint: string | null;
  enabled_in_browser_messaging: boolean | null;
  email_opt_in: boolean | null;
  additional_email_subscription_group_ids: string[] | null;
  email_subscription_group_ids: string[] | null;
  sms_opt_in: boolean | null;
  sms_subscription_group: string | null;
  sms_double_opt_in: boolean | null;
  default_email_subscription_group_id: string | null;
  default_email_subscription_group_name: string | null;
  historical_backfill_opt_in: boolean | null;
  historical_backfill_status: (typeof BridgeStatus)[keyof typeof BridgeStatus] | null;
  historical_backfill_start_time: string | null;
  historical_backfill_end_time: string | null;
  historical_backfill_completion_time: string | null;
  historical_customer_backfill_status: (typeof BridgeStatus)[keyof typeof BridgeStatus] | null;
  historical_customer_backfill_start_time: string | null;
  historical_customer_backfill_end_time: string | null;
  historical_customer_backfill_completion_time: string | null;
  setup_completed_at: string | null;
  direct_integration_selected: boolean | null;
  third_party_tag_manager: boolean | null;
  user_tracking_behavior: string | null;
  catalog_opt_in: boolean | null;
  catalog_item_id_type: string | null;
  catalog_include_only_active_products: boolean | null;
  sync_product_tags?: boolean | null;
  selected_product_tags?: string[] | null;
  sync_product_metafields?: boolean | null;
  /** Hash: metafield id => { namespace, key, type? } */
  selected_product_metafields?: Record<string, { namespace: string; key: string; type?: string }> | null;
  has_all_access_scopes?: boolean | null;
  missing_access_scopes?: string[] | null;
  sync_product_collections?: boolean | null;
  /** Shopify collection GIDs */
  selected_collection_ids?: string[] | null;
  /** Collection GraphQL GID => Shopify title */
  selected_collections?: Record<string, string> | null;
  /**
   * Shopify Market GID => market details (name, handle, catalog_*, countries with per-country
   * `email_subscription_group_*` when `shopify_markets_multiple_countries` is on).
   */
  selected_markets?: ShopifyBridgeSelectedMarkets | null;
  /** Shopify backup region GID for the shop's default market display */
  shopify_backup_region_id?: string | null;
  /** Human-readable backup region name from Shopify */
  shopify_backup_region_name?: string | null;
  /** Shop-primary locale from Shopify shopLocales */
  shopify_default_locale?: string | null;
  app_embed_active: boolean | null;
  sdk_version?: string | null;
  available_sdk_upgrade_versions?: string[] | null;
  sdk_version_updated_at?: string | null;
  /** Direct only: installed version from WebApp telemetry (the theme install, not the pixel `sdk_version`). */
  latest_seen_sdk_version?: string | null;
  synced_segments?: ShopifyBridgeSyncedSegment[] | null;
  /** True while segment sync is paused: webhooks removed, tracking preserved */
  segment_sync_paused?: boolean | null;
}

export const BridgeStatus = {
  ACTIVE: 1,
  INACTIVE: 2,
  IN_PROGRESS: 3,
  STOPPED: 4,
} as const;

export interface AlloysPartnersShopifyAppGroupIdBridgesBridgeIdResult {}

/**
 * Response shape for the PATCH bridge update. `segment_sync_results` is only present when the
 * request included `segmentIds`.
 */
export interface PatchAlloysPartnersShopifyAppGroupIdBridgesBridgeIdResult {
  segment_sync_results?: ShopifySegmentSyncResultEntry[];
}

export interface PatchAlloysPartnersShopifyAppGroupIdBridgesBridgeIdProps {
  /** The appGroupId that helps narrow down which bridge to update */
  appGroupId: string;
  /** The externalId that determines which bridge to update */
  bridgeId: string;
  /** Whether we are enabling user events or not */
  enabledUserEvents?: boolean;

  /** External ID type selected */
  externalIdType?: string;

  /** Custom ID endpoint for retrieving external id */
  customIdEndpoint?: string;

  /** Whether we are enabling in browser messaging or not */
  enabledInBrowserMessaging?: boolean;

  /** Whether we are enabling email subscription groups or not */
  emailOptIn?: boolean;

  /** List of additional email subscription groups */
  additionalEmailSubscriptionGroupIds?: (string | undefined)[];

  /** Whether we are enabling sms subscription groups or not */
  smsOptIn?: boolean;

  /** List of sms subscription groups */
  smsSubscriptionGroup?: string | undefined;

  /** Whether Braze-owned SMS double opt-in is enabled */
  smsDoubleOptIn?: boolean;

  /** Name for Email subscription group name created for this bridge */
  defaultEmailSubscriptionGroupName?: string;

  /** Whether we are enabling in historical backfill or not */
  historicalBackfillOptIn?: boolean;

  /** Whether integration is direct or standard */
  directIntegrationSelected?: boolean;

  /** Whether the storefront implements the SDK with a tag manager */
  thirdPartyTagManager?: boolean;

  /** Standard path SDK user tracking behavior */
  userTrackingBehavior?: string;

  /** Whether we are enabling email catalog sync or not */
  catalogOptIn?: boolean;

  /** Catalog item id type */
  catalogItemIdType?: string;

  /** Whether we are including only active products in catalog or not */
  catalogIncludeOnlyActiveProducts?: boolean;

  /** Whether to sync product tags */
  syncProductTags?: boolean;

  /** Product tags selected for syncing (sent as empty when syncProductTags is false) */
  selectedProductTags?: string[];

  /** Whether to sync product metafields */
  syncProductMetafields?: boolean;

  /** Product metafields selected for syncing: id => { namespace, key, type? } (sent as empty when syncProductMetafields is false) */
  selectedProductMetafields?: Record<string, { namespace: string; key: string; type?: string }>;

  /** Whether to sync product collections (collection_ids column) */
  syncProductCollections?: boolean;

  /** Shopify collection GIDs to sync (sent as empty when syncProductCollections is false) */
  selectedCollectionIds?: string[];

  /** Collection GID => title from Shopify (sent as empty when syncProductCollections is false) */
  selectedCollections?: Record<string, string>;

  /** Shopify Market GID => market details to sync */
  selectedMarkets?: ShopifyBridgeSelectedMarkets;

  /** Shopify backup region GID for default market display */
  shopifyBackupRegionId?: string;

  /** Human-readable backup region name from Shopify */
  shopifyBackupRegionName?: string;

  /** Shop-primary locale from Shopify */
  shopifyDefaultLocale?: string;

  /** SDK version to upgrade to */
  sdkVersion?: string;

  /**
   * Shopify segment GIDs to sync. Triggers a bulk backfill for each segment and populates
   * `segment_sync_results` on the response; every other field on this request is optional so a
   * segment-only sync can be submitted without resending the rest of the bridge's settings.
   */
  segmentIds?: string[];

  /**
   * When true with `segmentIds`, already-synced segments are re-imported additively (row "Sync now").
   */
  allowResync?: boolean;

  /**
   * Shopify segment GID to stop syncing without resending other bridge settings.
   */
  unsyncSegmentId?: string;

  /** Pause (true) or resume (false) segment sync; omitted when unchanged */
  segmentSyncPaused?: boolean;
}

export interface DeleteAlloysPartnersShopifyAppGroupIdBridgesBridgeIdProps {
  appGroupId: string;
  bridgeId: string;
}
